import _defineProperty from "/drone/src/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "/drone/src/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "/drone/src/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["enableTimePicker", "defaultDateValue", "dateValue", "onDateValueChange"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { useMemo } from 'react';
import { useDatePickerPanel } from './useDatePickerPanel';
import { useDatePickerState } from './useDatePickerState';
import { useDatePickerCallbacks } from './useDatePickerCallbacks';
import { formatDateTimeVector } from '../utils/formatDateTimeVector';
import { useControlledState } from '../../../system/hooks/useControlledState';
import { DateVectorRange } from '../utils/DateVectorRange';
import { useDatePickerSelectionEvents } from './useDatePickerSelectionEvents';

var externalizeValue = function externalizeValue(value, timeVector) {
  return value.convertToLocalDate(timeVector);
};

export var useDatePicker = function useDatePicker(_ref, ref) {
  var enableTimePicker = _ref.enableTimePicker,
      defaultDateValue = _ref.defaultDateValue,
      dateValue = _ref.dateValue,
      onDateValueChange = _ref.onDateValueChange,
      restProps = _objectWithoutProperties(_ref, _excluded);

  var _useControlledState = useControlledState(defaultDateValue, dateValue, onDateValueChange),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      externalValue = _useControlledState2[0],
      setExternalValue = _useControlledState2[1];

  var internalValue = useMemo(function () {
    return DateVectorRange.createFromLocalDate(externalValue);
  }, [externalValue]);

  var _useDatePickerPanel = useDatePickerPanel(restProps, ref),
      rootRef = _useDatePickerPanel.rootRef,
      inputRef = _useDatePickerPanel.inputRef,
      reallyOpen = _useDatePickerPanel.reallyOpen,
      toggleOpen = _useDatePickerPanel.toggleOpen,
      setClose = _useDatePickerPanel.setClose,
      arrowProps = _useDatePickerPanel.arrowProps,
      dropdownProps = _useDatePickerPanel.dropdownProps,
      portalProps = _useDatePickerPanel.portalProps,
      panelRestProps = _useDatePickerPanel.restProps;

  var _useDatePickerState = useDatePickerState({
    reallyOpen: reallyOpen,
    internalValue: internalValue
  }, panelRestProps),
      dirtyInternalValueVector = _useDatePickerState.dirtyInternalValueVector,
      setDirtyInternalValueVector = _useDatePickerState.setDirtyInternalValueVector,
      timeSelectorValue = _useDatePickerState.timeSelectorValue,
      setTimeSelectorValue = _useDatePickerState.setTimeSelectorValue,
      displayedDateVector = _useDatePickerState.displayedDateVector,
      setDisplayedDateVector = _useDatePickerState.setDisplayedDateVector,
      currentDateVector = _useDatePickerState.currentDateVector,
      monthYearSelectorOpen = _useDatePickerState.monthYearSelectorOpen,
      onMonthYearSelectorToggle = _useDatePickerState.onMonthYearSelectorToggle,
      stateRestProps = _useDatePickerState.restProps;

  var _useDatePickerCallbac = useDatePickerCallbacks({
    setDirtyInternalValueVector: setDirtyInternalValueVector,
    setExternalValue: setExternalValue,
    timeSelectorValue: timeSelectorValue,
    setTimeSelectorValue: setTimeSelectorValue,
    dirtyInternalValueVector: dirtyInternalValueVector,
    externalizeValue: externalizeValue
  }, stateRestProps),
      onDateSelectorChange = _useDatePickerCallbac.onDateSelectorChange,
      onTimeSelectorValueChange = _useDatePickerCallbac.onTimeSelectorValueChange,
      applyValue = _useDatePickerCallbac.applyValue,
      enableActionButtons = _useDatePickerCallbac.enableActionButtons,
      callbacksRestProps = _useDatePickerCallbac.restProps;

  var selectionEventProps = useDatePickerSelectionEvents({
    value: dirtyInternalValueVector,
    onChange: onDateSelectorChange
  });
  var formattedValue = useMemo(function () {
    if (internalValue.isEmpty()) {
      return '';
    }

    return formatDateTimeVector(internalValue.dateStart, enableTimePicker);
  }, [internalValue, enableTimePicker]);
  return {
    rootProps: _objectSpread(_objectSpread({}, callbacksRestProps), {}, {
      enableTimePicker: enableTimePicker,
      ref: rootRef
    }),
    inputProps: {
      readOnly: true,
      label: 'Select date',
      ref: inputRef,
      value: formattedValue,
      enableRootRef: true,
      enableFocusOnRootClick: true,
      onRootClick: toggleOpen
    },
    portalProps: portalProps,
    dropdownProps: dropdownProps,
    arrowProps: arrowProps,
    topBarProps: {
      displayedDateVector: displayedDateVector,
      onDisplayedDateVectorChange: setDisplayedDateVector,
      monthYearSelectorOpen: monthYearSelectorOpen,
      onMonthYearToggleClick: onMonthYearSelectorToggle
    },
    actionBarProps: {
      applyValue: applyValue,
      setClose: setClose
    },
    daySelectorProps: _objectSpread({
      displayedDateVector: displayedDateVector,
      onDisplayedDateVectorChange: setDisplayedDateVector,
      dirtyInternalValueVector: dirtyInternalValueVector,
      currentDateVector: currentDateVector
    }, selectionEventProps),
    monthYearSelectorProps: {
      displayedDateVector: displayedDateVector,
      onDisplayedDateVectorChange: setDisplayedDateVector,
      toggleMonthYearSelector: onMonthYearSelectorToggle
    },
    timeSelectorProps: {
      value: timeSelectorValue,
      onChange: onTimeSelectorValueChange
    },
    displayMonthYearSelector: monthYearSelectorOpen,
    open: reallyOpen,
    enableActionButtons: enableActionButtons,
    enableTimePicker: enableTimePicker
  };
};